/**
 * Duplicate Keyword Guard — чистые строки для карточки и истории Autopilot.
 * Отдельно от компонентов: vitest и правило react-refresh (§73, §101).
 */

import type { AutopilotSuggestion, DkgCardRow } from "@/lib/api-hooks";

export const DKG_PRESET = "Duplicate Keyword Guard";
export const DKG_PRESET_KEY = "duplicate_keyword_guard";

export function ago(iso: string, now: number = Date.now()): string {
  const d = (now - new Date(iso).getTime()) / 1000;
  if (d < 3600) return `${Math.max(1, Math.floor(d / 60))}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

const SKIP_TEXT: Record<string, string> = {
  already_paused: "Skipped — one copy was already paused before review.",
  campaign_became_multigeo: "Skipped — one of the campaigns now targets several countries.",
  superseded: "Skipped — the card composition changed on a later run.",
  campaign_excluded: "Skipped — one of the campaigns now has a broad keyword or Search Match.",
};

export function dkgSkipText(reason: string | null | undefined): string {
  return (reason && SKIP_TEXT[reason]) || "Skipped";
}

/** Текст колонки «note» в истории — по исходу и составу карточки. */
export function dkgHistoryNote(h: AutopilotSuggestion): string {
  const rows: DkgCardRow[] = h.payload.rows ?? [];
  if (h.status === "applied") {
    const win = h.payload.applied_winner_keyword_id ?? h.payload.winner_keyword_id;
    const w = rows.find((r) => r.keyword_id === win);
    const paused = h.payload.paused_keyword_ids?.length ?? Math.max(0, rows.length - 1);
    const where = w ? ` in ${w.campaign_name ?? `#${w.campaign_id}`} / ${w.ad_group_name ?? `#${w.ad_group_id}`}` : "";
    return `Paused ${paused} duplicate${paused === 1 ? "" : "s"}, kept ${w?.text ?? h.search_term}${where}`;
  }
  if (h.status === "dismissed") return "All pairings inside this card are suppressed";
  if (h.status === "skipped") {
    if (h.skip_reason === "already_paused") return "Already paused — one copy was stopped before review";
    return dkgSkipText(h.skip_reason);
  }
  return "";
}

/** «Auto · 2d ago» / «pavel@… · 3d ago» — макет: автор, потом давность. */
export function dkgHistoryMeta(h: AutopilotSuggestion, now: number = Date.now()): string {
  const when = h.acted_at ?? h.created_at;
  const who = h.acted_by_email ?? (h.status === "applied" ? "Auto" : "Rule");
  return `${who} · ${ago(when, now)}`;
}
